import * as React from "react";
import { useFieldState, useField } from "informedv4";
import * as classNames from "classnames";
import { inputClasses } from "../validation/ValidationClasses";
import { replaceDotsWithHyphens } from "../../misc/Helpers/StringHelper";

type InformedExtras = {
    validate?: (value: any, values?: any, props?: any) => string | undefined | Promise<string | undefined>;
    validateOn?: "change" | "blur" | "submit" | ("change" | "blur" | "submit")[];
    initialValue?: any;
    required?: boolean;
    [key: string]: any;
};

type TextBoxProps = {
    id?: string;
    field?: string;
    name?: string;
    inputAddon?: React.ReactNode;
    className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "name" | "className"> &
    InformedExtras;

export const TextBox: React.FC<TextBoxProps> = ({
    id,
    style,
    field,
    name,
    inputAddon,
    type,
    className,
    disabled,
    hidden,
    min,
    max,
    onBlur,
    placeholder,
    ...rest
}) => {

    const fieldName = field ?? name;
    if (!fieldName) {
        throw new Error("TextBox requires either `field` or `name`.");
    }

    const { error } = useFieldState(fieldName);


    const { fieldApi, fieldState, ref, userProps } = useField({
        type: type,
        name: name,
        validate: rest.validate,
        initialValue: rest.initialValue,
        required: rest.required,
    });

    const divClasses = classNames({ "input-group": inputAddon !== undefined });

    return (
        <>
            <div className={divClasses}>
                <input
                    {...userProps}
                    style={style}
                    disabled={disabled}
                    id={id}
                    data-automationid={replaceDotsWithHyphens(field)}
                    name={name}
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    onBlur={onBlur}
                    type={type}
                    ref={ref}
                    value={String(fieldState.value ?? '')}
                    className={classNames(inputClasses(error as string), className)}
                    onChange={(e) => fieldApi.setValue(e.currentTarget.value)}
                />
                {inputAddon ? <span className="input-group-addon">{inputAddon}</span> : null}
            </div>

            {error ? (
                <span data-automationid="ValidationError" className="field-validation-error">
                    {error as string}
                </span>
            ) : null}
        </>
    );
};
